<script setup lang="ts">
import { reactive, watch } from 'vue'
import type { WebsiteLeadCaptureState, WebsiteLeadSubmission } from '../../types'

const props = defineProps<{ leadCapture: WebsiteLeadCaptureState }>()
const emit = defineEmits<{
  submit: [submission: WebsiteLeadSubmission]
}>()

const form = reactive<WebsiteLeadSubmission>({
  name: '',
  email: '',
  phone: '',
  message: '',
  website: '',
})

function submit(): void {
  if (props.leadCapture.submitting) return

  emit('submit', { ...form })
}

watch(
  () => props.leadCapture.succeeded,
  (succeeded) => {
    if (!succeeded) return

    form.name = ''
    form.email = ''
    form.phone = ''
    form.message = ''
    form.website = ''
  },
)
</script>

<template>
  <form class="site:relative site:mt-8 site:grid site:gap-5" novalidate @submit.prevent="submit">
    <div class="site:absolute site:left-[-10000px] site:top-auto site:h-px site:w-px site:overflow-hidden" aria-hidden="true">
      <label>
        Website
        <input v-model="form.website" name="website" tabindex="-1" autocomplete="off" />
      </label>
    </div>

    <div class="site:grid site:gap-5 site:@min-[40rem]:grid-cols-2">
      <label class="site:grid site:gap-2 site:text-sm site:font-semibold">
        Name
        <input
          v-model="form.name"
          name="name"
          autocomplete="name"
          required
          class="site:min-h-12 site:w-full site:border site:border-(--site-color-border) site:bg-(--site-color-background) site:px-4 site:text-base site:font-normal site:text-(--site-color-text) site:outline-none site:transition site:duration-200 site:focus:border-(--site-color-primary) site:focus:ring-3 site:focus:ring-(--site-color-primary)/15"
          :style="{ borderRadius: 'var(--site-radius-sm)' }"
        />
        <span v-if="leadCapture.errors?.name" class="site:text-xs site:font-medium site:text-red-600">{{ leadCapture.errors.name }}</span>
      </label>

      <label class="site:grid site:gap-2 site:text-sm site:font-semibold">
        Email
        <input
          v-model="form.email"
          name="email"
          type="email"
          autocomplete="email"
          required
          class="site:min-h-12 site:w-full site:border site:border-(--site-color-border) site:bg-(--site-color-background) site:px-4 site:text-base site:font-normal site:text-(--site-color-text) site:outline-none site:transition site:duration-200 site:focus:border-(--site-color-primary) site:focus:ring-3 site:focus:ring-(--site-color-primary)/15"
          :style="{ borderRadius: 'var(--site-radius-sm)' }"
        />
        <span v-if="leadCapture.errors?.email" class="site:text-xs site:font-medium site:text-red-600">{{ leadCapture.errors.email }}</span>
      </label>
    </div>

    <label class="site:grid site:gap-2 site:text-sm site:font-semibold">
      Phone <span class="site:font-normal site:text-(--site-color-muted-text)">(optional)</span>
      <input
        v-model="form.phone"
        name="phone"
        type="tel"
        autocomplete="tel"
        class="site:min-h-12 site:w-full site:border site:border-(--site-color-border) site:bg-(--site-color-background) site:px-4 site:text-base site:font-normal site:text-(--site-color-text) site:outline-none site:transition site:duration-200 site:focus:border-(--site-color-primary) site:focus:ring-3 site:focus:ring-(--site-color-primary)/15"
        :style="{ borderRadius: 'var(--site-radius-sm)' }"
      />
      <span v-if="leadCapture.errors?.phone" class="site:text-xs site:font-medium site:text-red-600">{{ leadCapture.errors.phone }}</span>
    </label>

    <label class="site:grid site:gap-2 site:text-sm site:font-semibold">
      How can we help?
      <textarea
        v-model="form.message"
        name="message"
        rows="5"
        required
        class="site:w-full site:resize-y site:border site:border-(--site-color-border) site:bg-(--site-color-background) site:px-4 site:py-3 site:text-base site:font-normal site:leading-7 site:text-(--site-color-text) site:outline-none site:transition site:duration-200 site:focus:border-(--site-color-primary) site:focus:ring-3 site:focus:ring-(--site-color-primary)/15"
        :style="{ borderRadius: 'var(--site-radius-sm)' }"
      />
      <span v-if="leadCapture.errors?.message" class="site:text-xs site:font-medium site:text-red-600">{{ leadCapture.errors.message }}</span>
    </label>

    <div class="site:flex site:flex-wrap site:items-center site:gap-4">
      <button type="submit" class="site-site-button" :disabled="leadCapture.submitting">
        {{ leadCapture.submitting ? 'Sending...' : 'Send message' }}
      </button>
      <p
        v-if="leadCapture.message"
        class="site:text-sm site:font-medium"
        :class="leadCapture.succeeded ? 'site:text-(--site-color-primary)' : 'site:text-red-600'"
        role="status"
        aria-live="polite"
      >
        {{ leadCapture.message }}
      </p>
    </div>
  </form>
</template>
